import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { Box, Grid, Container, Typography, Button } from "@mui/material";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import { useUser } from '../UserContext';
import Page from "../components/Page";
import {
  AppWeeklySales,
  AppNewUsers,
  AppBugReports,
  AppItemOrders,
  AppCurrentVisits,
  AppWebsiteVisits,
  AppConversionRates,
  AppOrderTimeline,
  AppNewsUpdate,
} from "../sections/@dashboard/app";

export default function DashboardApp() {
  const [dashboardData, setDashboardData] = useState(null);
  const setTempCodeFn = httpsCallable(functions, "setTempAccessCode");
  const [tempAccessCode, setTempAccessCode] = useState(null);
  const { user } = useUser();

  // State for handling error/success messages
  const [responseMessage, setResponseMessage] = useState("");
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const sportTypesSnapshot = await getDocs(collection(db, "sportTypes"));
        const pennantsSnapshot = await getDocs(collection(db, "pennants"));
        const membersSnapshot = await getDocs(collection(db, "members"));

        const sportTypes = sportTypesSnapshot.docs.map((doc) => doc.data());
        const pennants = pennantsSnapshot.docs.map((doc) => doc.data());
        const members = membersSnapshot.docs.map((doc) => doc.data());

        setDashboardData({
          membersCount: members.length,
          pennantsCount: pennants.length,
          sportTypesByIncome: sportTypes.map((sportType) => ({
            label: sportType.name,
            value: sportType.income || 0,
          })),
          sportsByMembers: sportTypes.map((sportType) => ({
            label: sportType.name,
            value: sportType.membersCount || 0,
          })),
          notifications: members.map((member) => ({
            title: `${member.firstName} ${member.lastName} joined`,
            description: "New member registered",
          })),
          totalIncome: sportTypes.reduce(
            (total, sportType) => total + (sportType.income || 0),
            0
          ),
          unpaidCount: members.filter((member) => !member.lastInvoiceStatus)
            .length,
          todaySchedule: [],
          table: [],
        });
      } catch (error) {
        console.error("Error fetching data from Firestore: ", error);
      }
    };

    fetchDashboardData();
  }, [user?.uid]);

  const handleOpenFrontDoor = async () => {
    try {
      const openDoorFn = httpsCallable(functions, "openDoor");
      const response = await openDoorFn({ doorNumber: 1 }); // Door number 1
      setResponseMessage("Front Door opened successfully!");
      setIsError(false);
      console.log("Front door opened:", response.data);
    } catch (error) {
      setResponseMessage(`Error opening door - Check that Door Access Contoller is running - ${error.message}`);
      setIsError(true);
      console.error("Error opening door - Check that Door Access Contoller is running -", error);
    }
  };
  
  const handleTempAccessCode = async () => {
    try {
      const response = await setTempCodeFn();
      const { tempCode, expiresAt } = response.data;
      setTempAccessCode(response.data.tempCode);
      setResponseMessage(`Temporary access code: ${tempCode} (expires: ${new Date(expiresAt).toLocaleString()})`);
      setIsError(false);
      console.log("Temp access code set:", response.data);
    } catch (error) {
      setResponseMessage(`Error - Check that Door Access Contoller is running - ${error.message}`);
      setIsError(true);
      console.error("Error - Check that Door Access Contoller is running -", error);
    }
  };

  if (!dashboardData) {
    return <Typography>Loading dashboard data...</Typography>;
  }

  return (
    <Page className="no-print" title="Dashboard | Ballarat Squash and Racquetball">
      <Container className="no-print" maxWidth="xl">
        <Box sx={{ p: 2, pb: 5 }}>
          <Typography variant="h4">Hi, Welcome back</Typography>

          {/* Box for the buttons and response message */}
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: "#f5f5f5",
              boxShadow: 3,
              textAlign: "center",
            }}
          >
            {/* Button for opening front door */}
            <Box sx={{ mb: 2 }}>
              <Button
                variant="contained"
                onClick={handleOpenFrontDoor}
                sx={{
                  height: "50px",
                  width: "200px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  margin: "0 auto",
                }}
              >
                Open Front Door
              </Button>
            </Box>

            {/* Button for generating temp access code */}
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleTempAccessCode}
                sx={{
                  height: "50px",
                  width: "200px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  margin: "0 auto",
                }}
              >
                Temp Access Code
              </Button>
            </Box>

            {/* Display response message (success or error) */}
            {responseMessage && (
              <Typography
                sx={{
                  mt: 2,
                  color: isError ? "red" : "green",
                  fontWeight: "bold",
                }}
              >
                {responseMessage}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Dashboard Data Grid */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <AppWeeklySales data={dashboardData.membersCount} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <AppNewUsers data={dashboardData.pennantsCount} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <AppBugReports data={dashboardData.unpaidCount} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <AppItemOrders data={dashboardData.totalIncome} />
          </Grid>

          <Grid item xs={12} md={6} lg={8}>
            <AppWebsiteVisits data={dashboardData.table} />
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <AppCurrentVisits data={dashboardData.sportTypesByIncome} />
          </Grid>

          <Grid item xs={12}>
            <AppConversionRates data={dashboardData.sportsByMembers} />
          </Grid>

          <Grid item xs={12} md={6} lg={8}>
            <AppNewsUpdate data={dashboardData.notifications} />
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <AppOrderTimeline data={dashboardData.todaySchedule} />
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
